import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

import { outboundV2Service } from '@/services'
import { ApiResponse } from '@/services/api'
import { HouseBillScheduledSchema } from '@/services/OutboundV2Service'

export enum Business {
  OUTBOUND = 'outbound', // 出库扫描
  DELIVERY = 'delivery' // 发货扫描
}

interface ScanState {
  loading: boolean
  scanning: boolean
  houseBills: HouseBillScheduledSchema[]
  qrCode: string
  houseBill: HouseBillScheduledSchema | null
  activeBusiness: Business
}

interface ScanActions {
  toggleScanning: (scanning: boolean) => void
  getHouseBillsByScan: () => Promise<void>
  houseBillScan: (qrcode: string) => Promise<boolean>
  palletScan: (palletCode: string) => Promise<ApiResponse>
  clearHouseBill: () => void
  setActiveBusiness: (business: Business) => void
}

type ScanStore = ScanState & ScanActions

const initialState: ScanState = {
  loading: false,
  scanning: false,
  houseBills: [],
  qrCode: '',
  houseBill: null,
  activeBusiness: Business.OUTBOUND
}

export const useScanStore = create<ScanStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        toggleScanning: (scanning) => {
          set({ scanning })
        },
        getHouseBillsByScan: async () => {
          set({
            loading: true
          })
          try {
            const { success, data } = await outboundV2Service.fetchScheduled()
            if (success) {
              set({
                houseBills: data
              })
            }
          } finally {
            set({
              loading: false
            })
          }
        },

        houseBillScan: async (qrCode) => {
          const { success, data } = await outboundV2Service.outboundHouseBill(
            qrCode
          )

          if (success && data) {
            set({
              qrCode: qrCode,
              houseBill: data
            })
            return true
          } else {
            // await get().getHouseBillsByScan()
            return false
          }
        },

        palletScan: async (palletCode) => {
          set({ loading: true })
          try {
            const currentHouseBill = get().houseBill
            const hasPallet = currentHouseBill?.warehousePallets.find(
              (pallet) => pallet.palletCode === palletCode
            )
            if (hasPallet) {
              const { success, message } = await outboundV2Service.scanPallet(
                palletCode, get().activeBusiness
              )

              if (success) {
                get().houseBillScan(currentHouseBill!.houseBlNumber)

                set({
                  qrCode: palletCode
                  // houseBill: updatedHouseBill || currentHouseBill
                })
              } else {
                set({
                  qrCode: ''
                })
              }
              return { success, message }
            }
            return { success: false, message: '当前出库列表不存在此托盘' }
          } finally {
            set({ loading: false })
          }
        },

        clearHouseBill: () => {
          set({
            qrCode: '',
            houseBill: null
          })
        },

        setActiveBusiness: (business) => {
          set({ activeBusiness: business })
        }
      }),
      {
        name: 'scan-storage',
        partialize: (state) => ({
          qrCode: state.qrCode,
          houseBill: state.houseBill
        })
      }
    )
  )
)

export const useScanState = () => {
  const loading = useScanStore((state) => state.loading)
  const scanning = useScanStore((state) => state.scanning)
  const houseBills = useScanStore((state) => state.houseBills)
  const qrCode = useScanStore((state) => state.qrCode)
  const houseBill = useScanStore((state) => state.houseBill)
  const activeBusiness = useScanStore((state) => state.activeBusiness)
  return { loading, scanning, houseBills, houseBill, qrCode, activeBusiness }
}

export const useScanActions = () => {
  const toggleScanning = useScanStore((state) => state.toggleScanning)
  const getHouseBillsByScan = useScanStore((state) => state.getHouseBillsByScan)
  const houseBillScan = useScanStore((state) => state.houseBillScan)
  const palletScan = useScanStore((state) => state.palletScan)
  const clearHouseBill = useScanStore((state) => state.clearHouseBill)
  const setActiveBusiness = useScanStore((state) => state.setActiveBusiness)
  return {
    toggleScanning,
    getHouseBillsByScan,
    houseBillScan,
    palletScan,
    clearHouseBill,
    setActiveBusiness
  }
}
